import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
  TextField,
  Chip,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Avatar,
  Stack,
  Tooltip,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItem,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  CardActionArea,
  Select,
  Skeleton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SortIconDropdown, SortOption } from '@/components/SortIconDropdown/SortIconDropdown';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Description as DescriptionIcon,
  MedicalServices as MedicalIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  North as NorthIcon,
  South as SouthIcon,
  FileDownloadOutlined as FileDownloadIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import {
  Users as UsersIconPhosphor,
  PlusCircle as PlusCirclePhosphor,
  User as UserIcon
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useContentResponsive } from '@/contexts/ContentSpaceContext';
import {
  Person,
  CreatePersonInput,
  GetAllPeopleInput
} from '@/types/peopleTypes';
import { createPersonService } from '@/services/personService';
import { Template, GetAllTemplatesParams } from '@/types/templateTypes';
import * as templateService from '@/services/templateService';
import { AttioTextField } from '@/components/AttioForm';
import { createTranscriptionSession, getAllTranscriptionSessions } from '@/services/transcriptionSessionService';
import { TranscriptionSession } from '@/types/transcriptionSessionTypes';
import { createSpeakerManagementService } from '@/services/speakerManagementService';
import { AssociatePersonToSessionRequest } from '@/types/speakerManagementTypes';
import AttioTable, { SortDirection } from '@/components/AttioTable/AttioTable';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/components/Toast';
import { Users } from '@phosphor-icons/react';

// Fallback templates for when no real templates are available
const fallbackTemplatesForSelection = [
  {
    id: 'fallback-1',
    name: 'General Interview',
    description: 'A general interview template',
    icon: <DescriptionIcon />
  },
  {
    id: 'fallback-2',
    name: 'Basic Consultation',
    description: 'Basic consultation template',
    icon: <DescriptionIcon />
  }
];

// Helper to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60)
  );

  if (diffInHours < 24) {
    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1 hour ago';
    return `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return '1 day ago';
  if (diffInDays < 7) return `${diffInDays} days ago`;

  return date.toLocaleDateString();
};

// Helper function to get icon for template category
const getTemplateIcon = (categoryName?: string) => {
  if (!categoryName) return <DescriptionIcon />;

  const category = categoryName.toLowerCase();
  if (category.includes('medical') || category.includes('health'))
    return <MedicalIcon />;
  if (category.includes('legal') || category.includes('law'))
    return <DescriptionIcon />;
  if (
    category.includes('education') ||
    category.includes('school') ||
    category.includes('student')
  )
    return <SchoolIcon />;
  if (
    category.includes('business') ||
    category.includes('corporate') ||
    category.includes('company')
  )
    return <BusinessIcon />;

  return <DescriptionIcon />; // Default icon
};

// --- Session Creation with Person Association ---
interface CreateSessionWithPersonOptions {
  personId: string;
  templateId?: number;
  templateName?: string;
  userId: string;
  getAccessTokenSilently: any;
  getAccessTokenWithPopup: any;
  navigate: (path: string) => void;
  speakerManagementService: any;
}

const createSessionWithPerson = async (
  options: CreateSessionWithPersonOptions
): Promise<string | undefined> => {
  const {
    personId,
    templateId,
    templateName,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    navigate,
    speakerManagementService
  } = options;

  try {
    console.log(
      'Creating new session for person:',
      personId,
      'with template:',
      templateId,
      templateName
    );

    const sessionTitle = templateName
      ? `${templateName} Session - ${new Date().toLocaleString()}`
      : `Person Interview Session - ${new Date().toLocaleString()}`;

    // Create the session first
    const session = await createTranscriptionSession(
      {
        title: sessionTitle,
        templateId: templateId || null,
        sessionStartTime: new Date().toISOString()
      },
      userId,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const sessionId = session.id;

    if (!sessionId) {
      console.error('Failed to create session - no ID returned');
      return undefined;
    }

    console.log('Created session:', sessionId);

    // Associate the person with the session
    try {
      const associationRequest: AssociatePersonToSessionRequest = {
        personId: personId
        // Note: speakerIndex is optional and will be assigned during actual transcription
      };

      const associationSuccess =
        await speakerManagementService.associatePersonToSession(
          sessionId,
          associationRequest
        );

      if (!associationSuccess) {
        console.warn(
          'Failed to associate person with session, but continuing anyway'
        );
      } else {
        console.log(
          'Successfully associated person',
          personId,
          'with session',
          sessionId
        );
      }
    } catch (error) {
      console.error('Error associating person with session:', error);
      // Continue anyway - the session is created even if association fails
    }

    // Navigate to the session
    console.log('Navigating to /transcript/' + sessionId);
    navigate(`/transcript/${sessionId}`);
    return sessionId;
  } catch (error) {
    console.error('Failed to create session with person:', error);
    return undefined;
  }
};

// --- Select Template Dialog Component ---
interface SelectTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string | null, templateName: string | null) => void;
  templates: Array<{
    id: string;
    name: string;
    description: string;
    icon: React.ReactElement;
  }>;
  personName?: string;
}

const SelectTemplateDialog: React.FC<SelectTemplateDialogProps> = ({
  open,
  onClose,
  onSelect,
  templates,
  personName
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates;
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [templates, searchTerm]);

  const handleProceed = () => {
    if (selectedValue !== null) {
      const template = templates.find((t) => t.id === selectedValue);
      onSelect(selectedValue, template?.name || null);
      onClose();
      setSelectedValue(null);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedValue(null);
      setSearchTerm('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        New Recording for {personName}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Choose a template to structure the interview
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="template-search"
          label="Search templates"
          type="search"
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <List>
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => (
              <ListItemButton
                key={template.id}
                selected={selectedValue === template.id}
                onClick={() => setSelectedValue(template.id)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {React.cloneElement(
                    template.icon as React.ReactElement<{ fontSize?: string }>,
                    { fontSize: 'medium' }
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.description}
                />
              </ListItemButton>
            ))
          ) : (
            <ListItem sx={{ justifyContent: 'center', py: 3 }}>
              <Typography color="text.secondary">
                No templates match your search.
              </Typography>
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={selectedValue === null}
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Add Person Dialog Component ---
interface AddPersonDialogProps {
  open: boolean;
  onClose: () => void;
  onAddPerson: (input: CreatePersonInput) => Promise<void>;
  companyId?: string;
  loading?: boolean;
}

const AddPersonDialog: React.FC<AddPersonDialogProps> = ({
  open,
  onClose,
  onAddPerson,
  companyId,
  loading = false
}) => {
  const theme = useTheme();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const handleInternalSubmit = async () => {
    if (firstName.trim() && lastName.trim()) {
      const input: CreatePersonInput = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        companyId: companyId || ''
      };

      await onAddPerson(input);
      handleClose();
    }
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setNotes('');
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setNotes('');
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 32px rgba(0, 0, 0, 0.6)' 
            : '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography 
          sx={{ 
            fontSize: '18px',
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          Add New Person
        </Typography>
        <IconButton 
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: '6px',
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
            <AttioTextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="John"
              autoFocus
            />
            <AttioTextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder="Doe"
            />
          </Box>
          
          <AttioTextField
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="john.doe@example.com"
          />
          
          <AttioTextField
            label="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
            placeholder="+1 (555) 000-0000"
          />
          
          <AttioTextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional information..."
          />
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1.5
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            px: 2.5,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleInternalSubmit}
          variant="contained"
          disabled={!firstName.trim() || !lastName.trim() || loading}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            px: 3,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          {loading ? <CircularProgress size={20} /> : 'Add Person'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main PeoplePage Component ---
export const PeoplePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { shouldStackVertically, getGridSizes } = useContentResponsive();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const navigate = useNavigate();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { selectedUserRole, appUser } = useAppUser();
  const toast = useToast();

  // Initialize services
  const personService = useMemo(
    () => createPersonService(getAccessTokenSilently, getAccessTokenWithPopup),
    []
  );

  const speakerManagementService = useMemo(
    () =>
      createSpeakerManagementService(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      ),
    []
  );

  // State
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  type StatusFilter = 'all' | 'last7days';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  type ViewMode = 'grid' | 'list';
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  type SortBy =
    | 'displayName'
    | 'sessionCount'
    | 'lastSessionDate'
    | 'createdAt';
  const [sortBy, setSortBy] = useState<SortBy>('displayName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Sort options for the new dropdown
  const sortOptions: SortOption[] = [
    { key: 'displayName', label: 'Name' },
    { key: 'sessionCount', label: 'Sessions' },
    { key: 'lastSessionDate', label: 'Last Session' }
  ];
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [personActionMenuAnchorEl, setPersonActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedPersonForAction, setSelectedPersonForAction] =
    useState<Person | null>(null);
  const [sessionActionMenuAnchorEl, setSessionActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedSessionForAction, setSelectedSessionForAction] =
    useState<{ session: any; person: Person } | null>(null);
  const [isAddPersonDialogOpen, setIsAddPersonDialogOpen] = useState(false);
  const [isSelectTemplateDialogOpen, setIsSelectTemplateDialogOpen] =
    useState(false);
  const [currentPersonForNewIntake, setCurrentPersonForNewIntake] =
    useState<Person | null>(null);
  const [addPersonLoading, setAddPersonLoading] = useState(false);
  const [expandedPersons, setExpandedPersons] = useState<Set<string>>(new Set());
  const [personSessions, setPersonSessions] = useState<Map<string, TranscriptionSession[]>>(new Map());
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    person: Person | null;
  }>({
    open: false,
    person: null
  });

  // Template state
  const [templates, setTemplates] = useState<Template[]>([]);

  // Transform templates for SelectTemplateDialog
  const templatesForSelection = useMemo(() => {
    if (templates.length === 0) {
      // Fallback to basic templates if no real templates are available
      return fallbackTemplatesForSelection;
    }

    return templates.map((template) => ({
      id: template.id.toString(),
      name: template.name,
      description: template.description || 'No description available',
      icon: getTemplateIcon(template.category?.name)
    }));
  }, [templates]);

  // Search filter
  const peopleFilteredBySearch = useMemo(() => {
    if (!searchQuery) return people;
    const query = searchQuery.toLowerCase();
    return people.filter((person) => {
      const matchesName =
        person.displayName.toLowerCase().includes(query) ||
        person.firstName.toLowerCase().includes(query) ||
        person.lastName.toLowerCase().includes(query);
      const matchesContact =
        person.email?.toLowerCase().includes(query) ||
        person.phone?.toLowerCase().includes(query);
      return matchesName || matchesContact;
    });
  }, [people, searchQuery]);

  // Status filter
  const peopleFilteredByStatus = useMemo(() => {
    if (statusFilter === 'all') return peopleFilteredBySearch;
    if (statusFilter === 'last7days') {
      return peopleFilteredBySearch.filter((p) => {
        if (!p.lastSessionDate) return false;
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return new Date(p.lastSessionDate) >= sevenDaysAgo;
      });
    }
    return peopleFilteredBySearch;
  }, [peopleFilteredBySearch, statusFilter]);

  // Apply sorting
  const applySort = useCallback(
    (list: Person[]) => {
      const arr = [...list];
      arr.sort((a, b) => {
        let aVal: string | number = 0;
        let bVal: string | number = 0;
        if (sortBy === 'displayName') {
          aVal = a.displayName.toLowerCase();
          bVal = b.displayName.toLowerCase();
        } else if (sortBy === 'sessionCount') {
          aVal = a.sessionCount || 0;
          bVal = b.sessionCount || 0;
        } else if (sortBy === 'lastSessionDate') {
          aVal = a.lastSessionDate ? new Date(a.lastSessionDate).getTime() : 0;
          bVal = b.lastSessionDate ? new Date(b.lastSessionDate).getTime() : 0;
        } else {
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        }
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return arr;
    },
    [sortBy, sortDir]
  );

  const displayedPeople = useMemo(
    () => applySort(peopleFilteredByStatus),
    [peopleFilteredByStatus, applySort]
  );

  // Load people data
  const loadPeople = async () => {
    try {
      setLoading(true);
      if (!selectedUserRole?.companyId) {
        toast.showError('No company selected. Please select a role.');
        setLoading(false);
        return;
      }

      const input: GetAllPeopleInput = {
        companyId: selectedUserRole.companyId,
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'displayName',
        sortAscending: true
      };
      const peopleData = await personService.getAllPeople(input);
      setPeople(peopleData);
    } catch (error) {
      console.error('Failed to load people:', error);
      toast.showError('Failed to load people');
    } finally {
      setLoading(false);
    }
  };

  // Load templates data
  const loadTemplates = async () => {
    if (!appUser?.id) {
      console.warn('No user ID available for loading templates');
      return;
    }

    try {
      const params: GetAllTemplatesParams = {
        userId: appUser.id,
        includeArchived: false // Only get active templates
      };

      const templateData = await templateService.getAllTemplates(
        params,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      setTemplates(templateData);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.showWarning('Failed to load templates. Using default options.');
    }
  };

  // Setup header and menu actions
  useEffect(() => {
    setHeaderText(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <UsersIconPhosphor size={16} weight="regular" />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          People
        </Typography>
      </Box>
    );
    setMenuActions(
      isMobile ? (
        <IconButton
          onClick={() => setIsAddPersonDialogOpen(true)}
          sx={{
            backgroundColor: '#e6f226',
            color: '#030766',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#d4e022'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAddPersonDialogOpen(true)}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: '#e6f226',
            color: '#030766',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#d4e022',
              boxShadow: 'none'
            }
          }}
        >
          New Person
        </Button>
      )
    );

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [setHeaderText, setMenuActions, theme.palette.mode, isMobile]);

  // Load initial data
  useEffect(() => {
    loadPeople();
    loadTemplates();
  }, []);

  // Handle adding a new person
  const handleAddPerson = async (input: CreatePersonInput) => {
    try {
      setAddPersonLoading(true);
      const newPerson = await personService.createPerson(input);
      setPeople((prevPeople) => [newPerson, ...prevPeople]);
      toast.showSuccess(`${newPerson.displayName} added`);
    } catch (error) {
      console.error('Failed to add person:', error);
      toast.showError('Failed to add person');
    } finally {
      setAddPersonLoading(false);
    }
  };

  // Handle opening delete confirmation dialog
  const handleOpenDeleteDialog = (person: Person) => {
    setDeleteConfirmDialog({
      open: true,
      person: person
    });
    handleClosePersonActionMenu();
  };

  // Handle closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      person: null
    });
  };

  // Handle deleting a person
  const handleDeletePerson = async () => {
    const personToDelete = deleteConfirmDialog.person;
    if (!personToDelete) return;

    try {
      if (!selectedUserRole?.companyId) {
        toast.showError('No company selected. Please select a role.');
        handleCloseDeleteDialog();
        return;
      }

      const success = await personService.deletePerson(
        personToDelete.id,
        selectedUserRole.companyId
      );
      if (success) {
        setPeople((prevPeople) =>
          prevPeople.filter((p) => p.id !== personToDelete.id)
        );
        toast.showSuccess('Person deleted');
      } else {
        toast.showError('Person not found or already deleted');
      }
    } catch (error) {
      console.error('Failed to delete person:', error);
      toast.showError('Failed to delete person');
    }
    handleCloseDeleteDialog();
  };

  // Menu handlers
  const handleOpenPersonActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    person: Person
  ) => {
    setPersonActionMenuAnchorEl(event.currentTarget);
    setSelectedPersonForAction(person);
  };

  const handleClosePersonActionMenu = () => {
    setPersonActionMenuAnchorEl(null);
    setSelectedPersonForAction(null);
  };

  const handleStartNewIntakeForPerson = (person: Person) => {
    setCurrentPersonForNewIntake(person);
    setIsSelectTemplateDialogOpen(true);
    handleClosePersonActionMenu();
  };

  // Load sessions for a specific person
  const loadPersonSessions = async (personId: string) => {
    if (!appUser?.id || personSessions.has(personId)) return; // Don't reload if already loaded

    try {
      const paginatedResponse = await getAllTranscriptionSessions(
        {
          userId: appUser.id,
          pageNumber: 1,
          pageSize: 50,
          sortBy: 'sessionstarttime',
          sortAscending: false,
          personId: personId
        },
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      setPersonSessions(prev => new Map(prev).set(personId, paginatedResponse.sessions));
    } catch (error) {
      console.error('Failed to load sessions for person:', error);
    }
  };

  const handleTemplateSelectedForPerson = async (
    templateId: string | null,
    templateName: string | null
  ) => {
    if (!currentPersonForNewIntake || !appUser?.id) {
      console.error('Missing person or user information');
      toast.showError('Unable to create recording session');
      return;
    }

    const personId = currentPersonForNewIntake.id;
    console.log(
      `Creating recording session for ${
        currentPersonForNewIntake.displayName
      } with template: ${templateName || 'Custom'}`
    );

    try {
      // Create session with person association
      const sessionId = await createSessionWithPerson({
        personId: personId,
        templateId: templateId ? Number(templateId) : undefined,
        templateName: templateName || undefined,
        userId: appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        navigate,
        speakerManagementService
      });

      if (sessionId) {
        toast.showSuccess(`Session created for ${currentPersonForNewIntake.displayName}`);
      } else {
        toast.showError('Failed to create recording session');
      }
    } catch (error) {
      console.error('Error creating recording session:', error);
      toast.showError('Failed to create recording session');
    }

    // Reset state
    setIsSelectTemplateDialogOpen(false);
    setCurrentPersonForNewIntake(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Stack spacing={1}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Card
              key={i}
              sx={{
                background: theme.palette.background.paper,
                borderRadius: '8px',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Skeleton 
                variant="circular" 
                width={40} 
                height={40} 
                sx={{ 
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.06)'
                }} 
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton 
                  width="35%" 
                  height={20} 
                  sx={{ 
                    mb: 0.5,
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
                <Skeleton 
                  width="25%" 
                  height={16} 
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
              </Box>
              <Skeleton 
                variant="rounded" 
                width={24} 
                height={24} 
                sx={{ 
                  borderRadius: '4px',
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.06)'
                }} 
              />
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search and Controls Section */}
      <Box
        sx={{
          background: theme.palette.background.paper,
          px: isMobile ? 2 : 3,
          pt: 2,
          pb: 0
        }}
      >
        {/* Search and Sort Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2
          }}
        >
          <TextField
            placeholder="Search by name..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                height: 40,
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                },
                '&.Mui-focused': {
                  borderColor: '#6b7280',
                  boxShadow: 'none'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {!isMobile && (
            <>
              <SortIconDropdown
                options={sortOptions}
                currentSort={sortBy}
                currentDirection={sortDir}
                onSortChange={(key, direction) => {
                  if (key && direction) {
                    setSortBy(key as SortBy);
                    setSortDir(direction);
                  }
                }}
                size="small"
              />

              <Button
            onClick={() => {
              setShowFilters(!showFilters);
              if (!showFilters) {
                // When showing filters, reset to 'all' if needed
                if (statusFilter !== 'all') {
                  setStatusFilter('all');
                }
              } else {
                // When hiding filters, ensure we show all people
                setStatusFilter('all');
              }
            }}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              height: 40,
              width: 40,
              minWidth: 40,
              px: 0,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: showFilters ? 'rgba(75, 94, 219, 0.04)' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white'),
              color: showFilters ? '#4B5EDB' : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: showFilters ? 'rgba(75, 94, 219, 0.08)' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f8f9fa'),
                borderColor: showFilters ? '#4B5EDB' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db')
              }
            }}
          >
            <FilterListIcon sx={{ fontSize: '18px' }} />
          </Button>
            </>
          )}
        </Box>

        {/* Filter Pills - Only show when filters are enabled */}
        {showFilters && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {[
              { key: 'all', label: 'All', count: people.length },
              { key: 'last7days', label: 'Last 7 days', count: people.filter((p) => {
                if (!p.lastSessionDate) return false;
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                return new Date(p.lastSessionDate) >= sevenDaysAgo;
              }).length }
            ].map((tab) => (
              <Button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key as StatusFilter)}
                sx={{
                  px: 2.5,
                  py: 0.75,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  minHeight: 32,
                  background: statusFilter === tab.key 
                    ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#1f2937')
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'),
                  color: statusFilter === tab.key ? 'white' : theme.palette.text.secondary,
                  border: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    background: statusFilter === tab.key 
                      ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#111827')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb')
                  }
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Main Content Section */}
      <Box
        sx={{
          px: isMobile ? 2 : 3,
          pt: 0,
          pb: 3,
          flex: 1,
          overflow: 'auto',
          background: theme.palette.background.paper
        }}
      >
        {displayedPeople.length > 0 ? (
          <Stack spacing={1}>
            {displayedPeople.map((person, index) => (
              <Card
                key={person.id}
                onClick={() => navigate(`/people/${person.id}`)}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  opacity: 0,
                  animation: 'fadeSlideIn 0.3s ease forwards',
                  animationDelay: `${index * 30}ms`,
                  '@keyframes fadeSlideIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  },
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease'
                  }
                }}
              >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Left Side - Person Info */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.05)' 
                          : '#f3f4f6',
                        color: '#6b7280',
                        flexShrink: 0
                      }}
                    >
                      <UserIcon size={16} weight="regular" />
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          mb: 0.5,
                          lineHeight: 1.4,
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {person.displayName}
                      </Typography>
                      
                      <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, textAlign: 'left' }}>
                        {person.sessionCount || 0} recordings • Last: {person.lastSessionDate ? formatDate(person.lastSessionDate) : 'Never'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        ) : (
          <EmptyState
            icon={<Users size={22} weight="regular" />}
            title={searchQuery ? 'No people found' : 'No people yet'}
            description={
              searchQuery
                ? 'Try adjusting your search or filter criteria'
                : 'Add a new person to start managing contacts and recordings'
            }
          />
        )}
      </Box>

      {/* Dialogs */}
      <AddPersonDialog
        open={isAddPersonDialogOpen}
        onClose={() => setIsAddPersonDialogOpen(false)}
        onAddPerson={handleAddPerson}
        companyId={selectedUserRole?.companyId}
        loading={addPersonLoading}
      />

      {currentPersonForNewIntake && (
        <SelectTemplateDialog
          open={isSelectTemplateDialogOpen}
          onClose={() => {
            setIsSelectTemplateDialogOpen(false);
            setCurrentPersonForNewIntake(null);
          }}
          onSelect={handleTemplateSelectedForPerson}
          templates={templatesForSelection}
          personName={currentPersonForNewIntake?.displayName}
        />
      )}

      {/* Person Action Menu */}
      <Menu
        anchorEl={personActionMenuAnchorEl}
        open={Boolean(personActionMenuAnchorEl)}
        onClose={handleClosePersonActionMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (selectedPersonForAction)
              handleStartNewIntakeForPerson(selectedPersonForAction);
          }}
        >
          <ListItemIcon>
            <PlusCirclePhosphor size={20} weight="regular" />
          </ListItemIcon>
          <ListItemText 
            primaryTypographyProps={{ fontSize: '14px' }}
          >
            New Recording
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedPersonForAction)
              handleOpenDeleteDialog(selectedPersonForAction);
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText 
            primaryTypographyProps={{ fontSize: '14px' }}
          >
            Delete Person
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Session Action Menu */}
      <Menu
        anchorEl={sessionActionMenuAnchorEl}
        open={Boolean(sessionActionMenuAnchorEl)}
        onClose={() => {
          setSessionActionMenuAnchorEl(null);
          setSelectedSessionForAction(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (selectedSessionForAction) {
              navigate(`/transcript/${selectedSessionForAction.session.id}`);
              setSessionActionMenuAnchorEl(null);
              setSelectedSessionForAction(null);
            }
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Preview
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedSessionForAction) {
              console.log('Download session:', selectedSessionForAction.session.id);
              setSessionActionMenuAnchorEl(null);
              setSelectedSessionForAction(null);
            }
          }}
        >
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Download
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedSessionForAction) {
              console.log('Share session:', selectedSessionForAction.session.id);
              setSessionActionMenuAnchorEl(null);
              setSelectedSessionForAction(null);
            }
          }}
        >
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Share
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Person</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirmDialog.person?.displayName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All associated data with this person
            will be permanently removed.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeletePerson}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Person
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PeoplePage;
